import {gql} from '@apollo/client';
import {partition} from '@benchling/js-utils';
import {Stack} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {NucleotideType} from 'biojs/BioConstants';
import ModelTypeConstants, {OLIGO_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE} from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import FileStates from 'FileStates';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import MetadataDAO from 'metadata/MetadataDAO';
import notifications from 'notifications';
import * as RegistryUtil from 'registry/RegistryUtil';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import {oligoAwareFolderItemTypeMatchesTagSchema} from 'tag-schema/util/TagSchemaUtil';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipList from 'ui-library/chips/ChipList';
import Modal from 'ui-library/modals/Modal';
import {isServerRequestError} from 'util/Net';

import {
  PrimerSchemaSelectorModalQuery,
  PrimerSchemaSelectorModalQueryVariables,
} from './__generated/PrimerSchemaSelectorModalQuery';

type OligoNode = ExtractByTypename<PrimerSchemaSelectorModalQuery['oligos'][number], 'SequenceNode'>;

type InaccessibleNode = ExtractByTypename<
  PrimerSchemaSelectorModalQuery['oligos'][number],
  'InaccessibleInfoNode'
>;

type OligoOrInaccessibleList = PrimerSchemaSelectorModalQuery['oligos'][number];

interface ValidationsProps {
  oligos: Array<OligoOrInaccessibleList>;
  setCanSubmit: (canSubmit: boolean) => void;
}

function Validations({oligos, setCanSubmit}: ValidationsProps): JSX.Element {
  const [accessible, inaccessible] = partition<OligoNode, InaccessibleNode>(
    oligos,
    (o) => !isInaccessible(o)
  );
  const [editable, uneditable] = partition(accessible, (o) => o.permissions.writable);
  const [registered, unregistered] = partition(editable, (o) => RegistryUtil.isRegistered(o));
  const withSchema = unregistered.filter((o) => o.tagSchema);
  const canSubmit = registered.length === 0 && inaccessible.length === 0 && uneditable.length === 0;

  useEffect(() => {
    if (canSubmit) {
      setCanSubmit(true);
    }
  }, [canSubmit, setCanSubmit]);

  return (
    <Stack spacing="sm">
      {inaccessible.length > 0 && (
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text={
            <>
              <span>Cannot proceed because you lack permission to access the following primers.</span>
              <div className={css(styles.spacer)} />
              <ChipList
                itemIds={inaccessible.map((o) => o.invalidId)}
                chipSize={ChipSize.MINI}
                isVertical={false}
                showChipsAsLinks={false}
              />
            </>
          }
        />
      )}
      {uneditable.length > 0 && (
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text={
            <>
              <span>Cannot proceed because you lack permission to modify the following primers.</span>
              <div className={css(styles.spacer)} />
              <ChipList itemIds={uneditable.map((o) => o.id)} chipSize={ChipSize.MINI} isVertical={false} />
            </>
          }
        />
      )}
      {registered.length > 0 && (
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text={
            <>
              <span>Cannot proceed because the primers below are already registered.</span>
              <div className={css(styles.spacer)} />
              <ChipList itemIds={registered.map((o) => o.id)} chipSize={ChipSize.MINI} isVertical={false} />
            </>
          }
        />
      )}
      {canSubmit && withSchema.length > 0 && (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text="The primers below already have a schema set. If you proceed, existing schema fields will be preserved as custom fields."
          supportingInfo={
            <ChipList itemIds={withSchema.map((o) => o.id)} chipSize={ChipSize.MINI} isVertical={false} />
          }
        />
      )}
    </Stack>
  );
}

export const PRIMER_SCHEMA_SELECTOR_MODAL_QUERY = gql`
  query PrimerSchemaSelectorModalQuery($oligoIds: [StrictString!]!) @skipAudit @engComponent(name: MOLBIO) {
    oligos(ids: $oligoIds) {
      ... on SequenceNode {
        id
        name
        permissions {
          readable
          writable
        }
        folder {
          id
          source {
            ...SourceSummaryGraphQL
          }
        }
        tagSchema {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('SourceSummary').fragment}
`;

async function updateOligoSchemas(oligoIds: Array<string>, schemaId: string): Promise<void> {
  try {
    await MetadataDAO.bulkUpdateTagSchema(oligoIds, schemaId, false);
    notifications.success('Saved primers.');
    FileStates.get().updateSequenceVersions(oligoIds, {});
  } catch (e) {
    if (isServerRequestError(e)) {
      notifications.notifyError(e);
      return;
    }
    throw e;
  }
}

interface SchemaSelectorModalProps {
  oligoIds: Array<string>;
  oligoNucleotideTypes: Set<NucleotideType>;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export default function SchemaSelectorModal(props: SchemaSelectorModalProps): JSX.Element {
  const {oligoIds, oligoNucleotideTypes} = props;
  const [schemaId, setSchemaId] = useState<string | undefined>(undefined);
  const [canSubmit, setCanSubmit] = useState(false);

  // eligible schemas need to support all of these OligoAwareFolderItemTypes
  const schemaOligoAwareFolderItemTypesRequired = Array.from(oligoNucleotideTypes).map(
    (nucleotideType) => OLIGO_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE[nucleotideType]
  );

  return (
    <Modal
      buttons={[
        {
          submit: true,
          text: 'Save Primers',
          disabled: !schemaId || !canSubmit,
        },
      ]}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        updateOligoSchemas(props.oligoIds, schemaId!);
        props.onRequestSubmit();
      }}
      title="Select a schema"
      titleIcon={ModelTypeConstants.SEQUENCE_PRIMER}
    >
      <BenchlingQuery<PrimerSchemaSelectorModalQuery, PrimerSchemaSelectorModalQueryVariables>
        query={PRIMER_SCHEMA_SELECTOR_MODAL_QUERY}
        variables={{oligoIds}}
        renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
        fetchPolicy="network-only"
      >
        {(data) => <Validations oligos={data.oligos} setCanSubmit={setCanSubmit} />}
      </BenchlingQuery>
      <div className={css(styles.spacer)} />
      <TagSchemaSelector
        allowNone={true}
        onChange={(schema: TagSchema | null) => {
          setSchemaId(schema?.id);
        }}
        selectedTagSchemaId={schemaId}
        tagSchemaFilterFn={(schema: TagSchema) =>
          schemaOligoAwareFolderItemTypesRequired.every((oligoAwareFolderItemType) =>
            oligoAwareFolderItemTypeMatchesTagSchema(oligoAwareFolderItemType, schema)
          )
        }
      />
    </Modal>
  );
}

const styles = StyleSheet.create({spacer: {marginBottom: '10px'}});
